// Web Push notification utilities (G2.12 — admin dashboard port).
//
// Mirrors apps/pos/src/lib/push-notifications.ts byte-for-byte so the
// tenant OWNER / MANAGER gets the same opt-in flow on both the POS and
// the Admin dashboard. The only difference is the import path for the
// notification service.
//
// Backend endpoints (see services/api/src/notifications/notifications.controller.ts):
//   GET    /api/v1/notifications/push/vapid-public-key
//   POST   /api/v1/notifications/push/subscriptions
//   DELETE /api/v1/notifications/push/subscriptions

/** Check if the browser supports Web Push notifications. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/** Get current push notification permission state. */
export function getPushPermission(): NotificationPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'default';
  return Notification.permission;
}

/** Request push notification permission from the user. */
export async function requestPushPermission(): Promise<NotificationPermission> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'default';
  return Notification.requestPermission();
}

/** Convert a URL-safe base64 string to a Uint8Array (for VAPID applicationServerKey). */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from(rawData, (char) => char.charCodeAt(0));
}

/** Unsubscribe from push notifications. */
async function unsubscribeFromPush(
  registration: ServiceWorkerRegistration,
): Promise<boolean> {
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return true;
  return subscription.unsubscribe();
}

// ─── G2.12 — High-level enable / disable orchestration ─────────────────────

const PUSH_ENDPOINT_KEY = 'bbpos_push_endpoint';

export type EnablePushStatus = 'subscribed' | 'denied' | 'unsupported' | 'no-vapid' | 'error';

export interface EnablePushResult {
  status: EnablePushStatus;
  error?: string;
}

/**
 * Drive the full opt-in flow:
 *   1. Browser support → 2. Permission prompt → 3. VAPID key from backend
 *   → 4. SW registration → 5. PushManager.subscribe → 6. POST to backend
 *   → 7. Cache endpoint locally for the unsubscribe path.
 *
 * On any failure after the SW subscribe step we attempt to clean up the
 * local subscription so the next attempt starts fresh.
 */
export async function enablePushNotifications(): Promise<EnablePushResult> {
  if (!isPushSupported()) return { status: 'unsupported' };

  const permission = await requestPushPermission();
  if (permission !== 'granted') return { status: 'denied' };

  // Lazy-import the service to avoid a circular import on cold start.
  const { notificationService } = await import('@/services/notification.service');

  let publicKey: string | null = null;
  try {
    const res = await notificationService.getVapidPublicKey();
    publicKey = res.public_key;
  } catch (err) {
    return {
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to fetch VAPID key',
    };
  }
  // Build-time fallback for offline dev environments.
  if (!publicKey) {
    const buildKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
    if (buildKey) publicKey = buildKey;
  }
  if (!publicKey) return { status: 'no-vapid' };

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch (err) {
    return {
      status: 'error',
      error: err instanceof Error ? err.message : 'Service worker not ready',
    };
  }

  let subscription: PushSubscription;
  try {
    // Reuse an existing subscription if one is already on this device.
    const existing = await registration.pushManager.getSubscription();
    subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      }));
  } catch (err) {
    return {
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to subscribe with the browser',
    };
  }

  const subscriptionJson = subscription.toJSON();
  if (!subscriptionJson.endpoint) {
    await safeUnsubscribe(registration);
    return { status: 'error', error: 'Browser returned an empty subscription endpoint' };
  }

  try {
    await notificationService.registerPushSubscription(subscriptionJson);
  } catch (err) {
    await safeUnsubscribe(registration);
    return {
      status: 'error',
      error: err instanceof Error ? err.message : 'Failed to register subscription on the server',
    };
  }

  try {
    localStorage.setItem(PUSH_ENDPOINT_KEY, subscriptionJson.endpoint);
  } catch {
    /* localStorage unavailable (Safari private mode) — best-effort */
  }

  return { status: 'subscribed' };
}

/**
 * Tear down the push subscription on both the browser and the backend.
 * Idempotent — safe to call when nothing is currently subscribed.
 */
export async function disablePushNotifications(): Promise<void> {
  if (!isPushSupported()) return;

  let registration: ServiceWorkerRegistration | undefined;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    /* SW not registered — nothing to unsubscribe locally */
  }

  let endpoint: string | null = null;
  try {
    endpoint = localStorage.getItem(PUSH_ENDPOINT_KEY);
  } catch {
    /* ignore */
  }

  if (registration) {
    await safeUnsubscribe(registration);
  }

  if (endpoint) {
    try {
      const { notificationService } = await import('@/services/notification.service');
      await notificationService.unregisterPushSubscription(endpoint);
    } catch {
      /* Best-effort — the server-side prune-on-404 path will catch dead rows */
    }
    try {
      localStorage.removeItem(PUSH_ENDPOINT_KEY);
    } catch {
      /* ignore */
    }
  }
}

/** Internal: best-effort unsubscribe that never throws. */
async function safeUnsubscribe(registration: ServiceWorkerRegistration): Promise<void> {
  try {
    await unsubscribeFromPush(registration);
  } catch {
    /* ignore */
  }
}
